import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { RotateCcw, MessageSquare, Package, CheckCircle2, XCircle, Clock } from "lucide-react";
import ProductImage from "@/components/shared/ProductImage";

const TABS = [
    { key: "sent", label: "Sent · Awaiting", icon: Clock, color: "text-neutral-700" },
    { key: "accepted", label: "Accepted", icon: CheckCircle2, color: "text-green-700" },
    { key: "rejected", label: "Rejected", icon: XCircle, color: "text-red-700" },
];

const daysSince = (iso) => {
    if (!iso) return "—";
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d === 0) return "today";
    if (d === 1) return "1d";
    return `${d}d`;
};

const currency = (c) => (c === "USD" ? "$" : c === "EUR" ? "€" : "£");

export default function AdminArchive() {
    const [tab, setTab] = useState("sent");
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ catalog: 0, sent: 0, accepted: 0, rejected: 0 });
    const [loading, setLoading] = useState(false);
    const [rollingBack, setRollingBack] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [r, s] = await Promise.all([
                api.get("/admin/archive", { params: { tab } }),
                api.get("/admin/archive/summary"),
            ]);
            setRows(r.data);
            setSummary(s.data);
        } catch (e) {
            toast.error("Failed to load archive");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [tab]);

    const rollback = async (productId, clientId, clientName) => {
        const scope = clientId ? `just ${clientName}` : "ALL clients";
        if (!window.confirm(`Rollback this send (${scope})? This wipes the assignment as if it never happened and the design returns to the Catalog (Draft).`)) return;
        setRollingBack(`${productId}:${clientId || "all"}`);
        try {
            await api.post(`/products/${productId}/rollback`, { client_id: clientId || null });
            toast.success("Rolled back to Catalog (Draft)");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rollback failed");
        } finally {
            setRollingBack("");
        }
    };

    const rows_filtered = useMemo(() => rows.map((p) => ({
        ...p,
        _matches: (p.assignments || []).filter((a) => a.status === tab),
    })), [rows, tab]);

    const whatsappRemind = (a, p) => {
        const msg = encodeURIComponent(
            `Hi ${a.client_name || "there"} — quick nudge on this design we sent you ${daysSince(a.sent_at)} ago:\n\n` +
            `${p.title}\n` +
            `SKU: ${p.sku_code || "?"} · ${currency(p.currency)}${p.price?.toFixed(2)}/pc · MOQ 200\n\n` +
            `Would love to know if you'd like to move on this. — A V Corp`
        );
        window.open(`https://wa.me/?text=${msg}`, "_blank");
    };

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
            <div className="flex items-end justify-between mb-6 sm:mb-8">
                <div>
                    <div className="overline text-neutral-500">Lifecycle</div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none mt-2">Archive</h1>
                </div>
                <Link
                    to="/admin/catalog?lifecycle=catalog"
                    className="text-xs uppercase tracking-widest border border-black px-3 h-9 flex items-center hover:bg-black hover:text-white"
                    data-testid="archive-open-catalog"
                >
                    <Package className="h-3.5 w-3.5 mr-2" strokeWidth={1.5}/> Catalog · {summary.catalog}
                </Link>
            </div>

            <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
                {TABS.map((t) => {
                    const active = tab === t.key;
                    const Ic = t.icon;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            data-testid={`archive-tab-${t.key}`}
                            className={`flex items-center gap-2 px-4 h-11 uppercase tracking-widest text-[11px] font-semibold border-b-2 -mb-px transition-colors ${
                                active ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"
                            }`}
                        >
                            <Ic className={`h-3.5 w-3.5 ${active ? t.color : ""}`} strokeWidth={1.5}/>
                            {t.label}
                            <span className={`ml-1 tabular-nums text-[10px] font-body ${active ? "text-black" : "text-neutral-400"}`}>· {summary[t.key] || 0}</span>
                        </button>
                    );
                })}
            </div>

            {loading && <div className="text-sm text-neutral-500 font-body">Loading…</div>}

            {!loading && rows_filtered.length === 0 && (
                <div className="border border-dashed border-border p-12 text-center">
                    <div className="overline text-neutral-500">Empty</div>
                    <div className="font-display text-2xl mt-2">Nothing in {tab}</div>
                    <div className="text-sm text-neutral-500 mt-2 font-body">
                        {tab === "sent" && "Send designs from the Catalog to see them land here."}
                        {tab === "accepted" && "Once a client places an order, the design lands here."}
                        {tab === "rejected" && "Designs a client marked as Not Interested show up here."}
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {rows_filtered.map((p) => (
                    <div key={p.id} className="border border-border bg-white flex flex-col md:flex-row gap-0 md:gap-4" data-testid={`archive-row-${p.id}`}>
                        <div className="w-full md:w-40 aspect-[3/4] md:aspect-auto md:h-44 shrink-0 bg-neutral-50 border-b md:border-b-0 md:border-r border-border overflow-hidden">
                            <ProductImage product={p} className="w-full h-full object-cover"/>
                        </div>
                        <div className="p-4 flex-1 min-w-0">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="overline text-neutral-500">
                                        <span className="text-neutral-400">{p.brand}</span>
                                        {p.sku_code && <span className="text-black ml-2 font-mono">{p.sku_code}</span>}
                                        {p.category && <span className="text-neutral-400 ml-2">· {p.category}</span>}
                                    </div>
                                    <div className="font-body text-sm mt-1 line-clamp-2 pr-4">{p.title}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display text-xl tabular-nums leading-none">{currency(p.currency)}{p.price?.toFixed(2)}<span className="text-[10px] uppercase tracking-widest text-neutral-500 ml-1">/pc</span></div>
                                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-1">MOQ 200 pcs</div>
                                </div>
                            </div>

                            <div className="mt-3 border-t border-border pt-3 space-y-2">
                                {p._matches.map((a) => (
                                    <div key={a.client_id} className="flex flex-wrap items-center gap-2" data-testid={`assign-row-${p.id}-${a.client_login_id}`}>
                                        <span className="uppercase tracking-widest text-[10px] font-semibold px-2 py-1 text-white" style={{ background: a.client_color || "#0A0A0A" }}>
                                            {a.client_name}
                                        </span>
                                        {tab === "sent" && (
                                            <span className="text-[11px] font-body text-neutral-500">
                                                Sent <span className="tabular-nums text-black">{daysSince(a.sent_at)}</span> ago
                                            </span>
                                        )}
                                        {tab === "accepted" && (
                                            <span className="text-[11px] font-body text-green-700">
                                                Ordered <span className="tabular-nums">{daysSince(a.decided_at)}</span> ago
                                                {a.order_number && (
                                                    <Link to="/admin/orders" className="ml-2 font-mono underline">{a.order_number}</Link>
                                                )}
                                            </span>
                                        )}
                                        {tab === "rejected" && (
                                            <span className="text-[11px] font-body text-red-700">
                                                Passed <span className="tabular-nums">{daysSince(a.decided_at)}</span> ago
                                            </span>
                                        )}
                                        <div className="flex-1"/>
                                        {tab === "sent" && (
                                            <button
                                                type="button"
                                                onClick={() => whatsappRemind(a, p)}
                                                data-testid={`remind-${p.id}-${a.client_login_id}`}
                                                className="uppercase tracking-widest text-[10px] font-semibold border border-black px-3 h-8 flex items-center gap-1.5 hover:bg-black hover:text-white"
                                                title="Send a WhatsApp nudge"
                                            >
                                                <MessageSquare className="h-3 w-3" strokeWidth={1.5}/> Remind
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            disabled={rollingBack === `${p.id}:${a.client_id}`}
                                            onClick={() => rollback(p.id, a.client_id, a.client_name)}
                                            data-testid={`rollback-${p.id}-${a.client_login_id}`}
                                            className="uppercase tracking-widest text-[10px] font-semibold border border-red-700 text-red-700 px-3 h-8 flex items-center gap-1.5 hover:bg-red-700 hover:text-white disabled:opacity-50"
                                            title="Wipe this send as if it never happened"
                                        >
                                            <RotateCcw className="h-3 w-3" strokeWidth={1.5}/>
                                            {rollingBack === `${p.id}:${a.client_id}` ? "…" : "Rollback"}
                                        </button>
                                    </div>
                                ))}
                                {p._matches.length > 1 && (
                                    <div className="pt-1">
                                        <button
                                            type="button"
                                            disabled={rollingBack === `${p.id}:all`}
                                            onClick={() => rollback(p.id, null, "all clients")}
                                            data-testid={`rollback-all-${p.id}`}
                                            className="uppercase tracking-widest text-[10px] font-semibold border border-red-700 text-red-700 px-3 h-8 hover:bg-red-700 hover:text-white disabled:opacity-50"
                                        >
                                            {rollingBack === `${p.id}:all` ? "…" : "Rollback all assignments"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
